// src/pages/fleet/FleetDashboardPage.tsx — analytics + Ola/Uber-style live tracking.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import FleetAiPanel from './FleetAiPanel';
import type { DivIcon, Layer, Marker } from 'leaflet';
import { getVehicleRejections, getFleetDashboard, getTrips, getFleetAlerts, getRoadPath, getIfmsQueue, subscribeToStream } from '../../services/fleetApi';
import type { FleetDashboardStats, LatLngTuple } from '../../services/fleetApi';
import type { FleetPermission, FleetSourceStatus, FleetStreamMessage, ISODate, Trip, TripAlert, Vehicle } from '../../types';
import { apiError } from '../../utils/errors';
import { useAuth } from '../../store/AuthContext';
import { can } from './permissions';
import { css } from '../../utils/css';
import useIsMobile from './useIsMobile';
import { createFleetMap, zoomTo, plantMarker, PLANT } from './mapKit';
import type { FleetMap } from './mapKit';

const NAVY = '#0A3A7A', ACCENT = '#1560BD';

/** Dashboard vehicles carry extra health fields the shared Vehicle type lacks. */
type DashVehicle = Vehicle & { dataDelayMinutes?: number | null };

/** The backend's location broadcast also sends gpsAt + healthStatus, and there is a
 *  tripCancelled event — neither is in the shared FleetStreamMessage union. */
type LocationMsg = Extract<FleetStreamMessage, { type: 'location' }> & { gpsAt?: ISODate; healthStatus?: string };
type TripCancelledMsg = { type: 'tripCancelled'; tripId: string; vehicleId: string; tripNumber?: string; reason?: string | null; at: ISODate };
type StreamMsg = Exclude<FleetStreamMessage, { type: 'location' }> | LocationMsg | TripCancelledMsg;

/** Shared TripAlert.trip is `Partial<Trip> & { vehicle?: Partial<Vehicle> }`, which
 *  intersects to a *full* Vehicle; stream alerts only know the vehicle number. */
type LiveAlert = Omit<TripAlert, 'trip'> & { trip?: Omit<Partial<Trip>, 'vehicle'> & { vehicle?: Partial<Vehicle> } };

type VState = 'Offline' | 'Delayed' | 'GPS Gap' | 'Moving' | 'Halted' | 'Parked' | 'Provider down' | 'Unreliable';

// The server now says what is going on rather than leaving it to be inferred
// from the age of the last fix. Two of these did not exist before and are the
// point of the change: a parked truck whose device is fine reads "Parked", not
// "GPS Gap", and a truck whose provider is unreachable is not blamed for it.
const STATE_OF: Record<string, VState> = {
  LIVE: 'Moving',
  STATIONARY_DEVICE_ONLINE: 'Parked',
  GPS_DELAYED: 'Delayed',
  DEVICE_OFFLINE: 'Offline',
  PROVIDER_UNAVAILABLE: 'Provider down',
  GPS_UNRELIABLE: 'Unreliable',
};

function vState(v: Vehicle): VState {
  if (v.trackingState && STATE_OF[v.trackingState]) return STATE_OF[v.trackingState];
  // Older payloads, and the SSE location message, still carry only healthStatus.
  if (v.healthStatus === 'OFFLINE') return 'Offline';
  if (v.healthStatus === 'DELAYED') return 'Delayed';
  if (v.healthStatus === 'GPS_GAP') return 'GPS Gap';
  if (v.healthStatus === 'MOVING') return 'Moving';
  return (v.currentSpeed || 0) > 5 ? 'Moving' : 'Halted';
}
function vColor(v: Vehicle): string {
  const s = vState(v);
  if (s === 'Moving') return '#22A559';
  if (s === 'Halted' || s === 'Parked') return '#E8941A';
  if (s === 'Delayed') return '#B42318';
  if (s === 'GPS Gap') return '#7C3AED';
  if (s === 'Unreliable') return '#B42318';
  if (s === 'Provider down') return '#6B7280';
  return '#9AA5B1';
}

/** One line saying how fresh this is, in the words an operator would use. */
function freshness(v: Vehicle): string {
  const bits: string[] = [];
  if (v.heartbeatAgeMinutes != null) {
    bits.push(v.heartbeatAgeMinutes < 2 ? 'device reporting now' : `device last heard ${v.heartbeatAgeMinutes} min ago`);
  }
  if (v.movedAgeMinutes != null) bits.push(`last moved ${v.movedAgeMinutes} min ago`);
  if (v.recentRejections) bits.push(`${v.recentRejections} fix(es) refused in the last hour`);
  return bits.join(' · ');
}
function km(a: number, b: number, c: number, d: number): number { const R = 6371, r = (x: number) => x * Math.PI / 180; const dLat = r(c - a), dLon = r(d - b); const x = Math.sin(dLat / 2) ** 2 + Math.cos(r(a)) * Math.cos(r(c)) * Math.sin(dLon / 2) ** 2; return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)); }
function truckIcon(color: string): DivIcon | null {
  if (!window.L) return null;
  return window.L.divIcon({ className: 'ft', html: `<div style="width:34px;height:34px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;font-size:18px;box-shadow:0 3px 10px rgba(0,0,0,.4);border:2.5px solid #fff">🚛</div>`, iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -18] });
}

export default function FleetDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // How many trips still owe IFMS something (the queue retries them automatically).
  const [ifmsPending, setIfmsPending] = useState<number | null>(null);
  const isMobile = useIsMobile();
  const mapEl = useRef<HTMLDivElement>(null), mapRef = useRef<FleetMap | null>(null), markers = useRef(new Map<string, Marker>()), routeLayers = useRef<Layer[]>([]);
  // The planned road of every running trip, kept on the map rather than drawn
  // on click. Keyed by trip id so each road is fetched once and survives the
  // fifteen-second refresh; routeLayers above stays the selected-trip overlay.
  const tripRoads = useRef(new Map<string, Layer[]>());
  // The opening view is set once, when there is finally something to look at.
  const framed = useRef(false);
  const [stats, setStats] = useState<Partial<FleetDashboardStats>>({});
  const [vehicles, setVehicles] = useState<DashVehicle[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [alerts, setAlerts] = useState<LiveAlert[]>([]);
  const [sourceStatuses, setSourceStatuses] = useState<FleetSourceStatus[]>([]);
  const [ready, setReady] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const [diag, setDiag] = useState<{ id: string; label: string } | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [refreshing, setRefreshing] = useState(false);
  // Set when the last refresh did not reach the server, so the screen can say so
  // instead of looking freshly loaded.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  // trip lookup: vehicleId → active trip
  const tripByVeh: Record<string, Trip> = {};
  trips.filter(t => t.status === 'ACTIVE').forEach(t => { tripByVeh[t.vehicleId] = t; });

  useEffect(() => {
    if (!window.L || mapRef.current || !mapEl.current) return;
    const map = createFleetMap(mapEl.current, [21.5, 78.9], 5);
    plantMarker().addTo(map);          // the plant every trip starts from
    mapRef.current = map; setReady(true);
    return () => { map.remove(); mapRef.current = null; markers.current.clear(); };
  }, []);

  const upsert = (v: DashVehicle) => {
    const map = mapRef.current; if (!map || v.currentLat == null || v.currentLng == null) return;
    // A map only exists once Leaflet is loaded, so truckIcon() is never null here.
    const icon = truckIcon(vColor(v)) as DivIcon;
    // The popup says how old this is, in words. A pin on a map looks equally
    // current whether the fix is thirty seconds or three hours old, and that is
    // exactly the thing an operator must not have to guess at.
    const fresh = freshness(v);
    const pop = `<b>${v.vehicleNumber}</b><br/>${v.driverName || ''}`
      + `<br/>${v.trackingLabel || v.healthLabel || vState(v)}`
      + (fresh ? `<br/><span style="color:#5b6472">${fresh}</span>` : '')
      + (v.lastIssueType ? `<br/>Issue: ${v.lastIssueType}` : '');
    let m = markers.current.get(v.id);
    if (m) { m.setLatLng([v.currentLat, v.currentLng]); m.setIcon(icon); m.getPopup() ? m.setPopupContent(pop) : m.bindPopup(pop); }
    else {
      const mk = window.L.marker([v.currentLat, v.currentLng], { icon }).addTo(map).bindPopup(pop);
      // Clicking a truck on the map zooms straight to it — no repeated zoom-in needed.
      mk.on('click', () => { setSel(v.id); zoomTo(map, mk.getLatLng()); });
      markers.current.set(v.id, mk);
    }
  };

  const loadAll = () => {
    setRefreshing(true);
    Promise.allSettled([
      getFleetDashboard().then(r => { setStats(r.data.stats || {}); setVehicles(r.data.vehicles || []); setSourceStatuses(r.data.sourceStatuses || []); (r.data.vehicles || []).forEach(upsert); }),
      getTrips().then(r => setTrips(r.data.trips || [])),
      getFleetAlerts({ resolved: 'false' }).then(r => setAlerts(r.data.alerts || [])),
    ]).then((results) => {
      // The timestamp is a claim that what is on screen is current, so only a
      // clean refresh earns a new one. It used to be stamped in .finally(), which
      // meant a dead network still moved the clock forward and said nothing —
      // on the screen the control room makes decisions from.
      const failed = results.filter((r) => r.status === 'rejected').length;
      setRefreshFailed(failed > 0);
      if (!failed) setUpdatedAt(new Date());
      setTimeout(() => setRefreshing(false), 500);
    });
  };
  useEffect(() => { if (ready) loadAll(); }, [ready]);
  useEffect(() => {
    if (!can(user, 'trip_list')) return;
    const pull = () => getIfmsQueue().then((r) => setIfmsPending(r.data.summary.pending)).catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!ready) return;
    const unsub = subscribeToStream((msg: StreamMsg) => {
      if (msg.type === 'location') {
        setVehicles(prev => {
          const nx = prev.map(v => v.id === msg.vehicleId ? {
            ...v,
            currentLat: msg.latitude,
            currentLng: msg.longitude,
            currentSpeed: msg.speed,
            status: msg.status,
            lastPingAt: msg.at,
            lastGpsAt: msg.gpsAt || msg.at,
            healthStatus: msg.healthStatus || v.healthStatus,
          } : v);
          const f = nx.find(v => v.id === msg.vehicleId); if (f) upsert(f); return nx;
        });
      } else if (msg.type === 'alert' && msg.alerts) {
        setAlerts(prev => [...msg.alerts.map((a, i) => ({ id: `${a.type}-${msg.vehicleNumber}-${Date.now()}-${i}`, type: a.type, severity: a.severity, message: a.message, trip: { vehicle: { vehicleNumber: msg.vehicleNumber } }, vehicle: { vehicleNumber: msg.vehicleNumber } })), ...prev].slice(0, 30));
      } else if (msg.type === 'tripCompleted' || msg.type === 'tripCancelled') {
        loadAll();
      }
    });
    return unsub;
  }, [ready]);

  const focus = (v: DashVehicle) => {
    setSel(v.id);
    const map = mapRef.current; if (!map || v.currentLat == null || v.currentLng == null) return;
    const m = markers.current.get(v.id);
    zoomTo(map, m ? m.getLatLng() : [v.currentLat, v.currentLng]);
    if (m) m.openPopup();
  };

  const clearRoute = () => { const map = mapRef.current; if (map) routeLayers.current.forEach(l => map.removeLayer(l)); routeLayers.current = []; };

  // Every running trip's road, always on the map. Without it the dashboard was
  // a set of pins: you could see where the trucks were and not where any of
  // them was going, and the road only appeared for one trip at a time, on a
  // click. Drawn muted and underneath, so the trucks stay the thing you read.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !window.L) return;
    const active = trips.filter((t) => t.status === 'ACTIVE');
    const wanted = new Set(active.map((t) => t.id));

    tripRoads.current.forEach((layers, id) => {
      if (wanted.has(id)) return;
      layers.forEach((l) => map.removeLayer(l));
      tripRoads.current.delete(id);
    });

    active.forEach(async (t) => {
      if (tripRoads.current.has(t.id)) return;
      // Claimed before the await so a refresh landing mid-fetch cannot draw
      // the same road twice.
      tripRoads.current.set(t.id, []);
      const road = await getRoadPath(t.fromLat, t.fromLng, t.toLat, t.toLng);
      // This effect re-runs whenever a GPS fix moves a truck, so a refresh
      // lands mid-fetch routinely. Aborting here used to leave the claim in
      // place, and the entry stayed an empty array no road was ever drawn
      // into — the trip simply had no route for the rest of the session. The
      // claim is released instead, so the next run draws it.
      if (!mapRef.current) { tripRoads.current.delete(t.id); return; }
      // Someone removed the trip while we were waiting; leave it removed.
      if (!tripRoads.current.has(t.id)) return;
      const L = window.L;
      const path: LatLngTuple[] = road || [[t.fromLat, t.fromLng], [t.toLat, t.toLng]];
      // Drawn to be seen from a district away. The detail page's hairline dash
      // reads well over one route at street zoom and disappears here: on a pale
      // basemap a white casing under a 1-in-7 grey dash is two invisible lines.
      const layers: Layer[] = [
        L.polyline(path, { color: '#FFFFFF', weight: 9, opacity: 0.95 }).addTo(map),
        L.polyline(path, { color: '#3D6FB5', weight: 4.5, opacity: 0.95, dashArray: '10 8', lineCap: 'round' }).addTo(map),
        L.marker([t.toLat, t.toLng], {
          icon: L.divIcon({ className: 'rt-pin', html: '<div style="font-size:20px">🏪</div>', iconSize: [24, 24], iconAnchor: [12, 12] }),
        }).addTo(map).bindPopup(`<b>${t.vehicle?.vehicleNumber || t.tripNumber}</b><br/>Going to ${t.toLocation}`),
      ];
      tripRoads.current.set(t.id, layers);
      frame();
    });

    // Open where the work is. The map started on the whole country, which is
    // the right view only if you do not know where your trucks are: every
    // operator had to pan and zoom to Telangana before the screen told them
    // anything. Fitted once, so it never argues with someone who has moved on.
    function frame() {
      if (framed.current || !mapRef.current) return;
      const pts: LatLngTuple[] = [];
      active.forEach((t) => {
        pts.push([t.toLat, t.toLng]);
        const v = vehicles.find((x) => x.id === t.vehicleId);
        if (v?.currentLat != null && v.currentLng != null) pts.push([v.currentLat, v.currentLng]);
      });
      pts.push([PLANT.lat, PLANT.lng]);
      if (pts.length < 2) return;
      const b = window.L.latLngBounds(pts).pad(0.15);
      mapRef.current.fitBounds(b, { animate: false });
      // Recentre comes back here rather than to the whole country.
      mapRef.current.fleetSetHome(b);
      framed.current = true;
    }

    frame();
  }, [ready, trips, vehicles]);

  // Track = ONE road route split at current position: green = travelled, grey = remaining (Ola/Uber style)
  // view 'vehicle' = close up on the truck (route still drawn); 'route' = whole trip in view.
  const trackRoute = async (v: DashVehicle, view: 'vehicle' | 'route' = 'route') => {
    const t = tripByVeh[v.id]; const map = mapRef.current;
    if (!t || !map || !window.L) { focus(v); return; }
    if (view === 'vehicle') focus(v);   // zoom right away, don't wait for the road path
    setSel(v.id); clearRoute();
    const L = window.L;
    const road = await getRoadPath(t.fromLat, t.fromLng, t.toLat, t.toLng);
    const path: LatLngTuple[] = road || [[t.fromLat, t.fromLng], [t.toLat, t.toLng]];
    // split the road at the point nearest the truck's current position
    let idx = path.length - 1;
    if (v.currentLat != null && v.currentLng != null) { let best = Infinity; for (let i = 0; i < path.length; i++) { const d = (path[i][0] - v.currentLat) ** 2 + (path[i][1] - v.currentLng) ** 2; if (d < best) { best = d; idx = i; } } }
    const done = path.slice(0, idx + 1), left = path.slice(idx);
    if (left.length > 1) routeLayers.current.push(L.polyline(left, { color: '#B4C0CC', weight: 6, opacity: 0.75 }).addTo(map));  // remaining (light)
    if (done.length > 1) routeLayers.current.push(L.polyline(done, { color: '#22A559', weight: 6, opacity: 0.95 }).addTo(map));  // travelled (green)
    const pin = (lat: number, lng: number, emoji: string, label: string) => L.marker([lat, lng], { icon: L.divIcon({ className: 'rt-pin', html: `<div style="font-size:22px">${emoji}</div>`, iconSize: [26, 26], iconAnchor: [13, 13] }) }).addTo(map).bindPopup(label);
    routeLayers.current.push(pin(t.fromLat, t.fromLng, '🏭', `<b>START</b><br/>${t.fromLocation}`));
    routeLayers.current.push(pin(t.toLat, t.toLng, '🏪', `<b>DESTINATION</b><br/>${t.toLocation}`));
    map.fleetSetHome(path);   // Recentre button shows the whole route
    if (view === 'route') {
      map.flyToBounds(path, { padding: [50, 50], duration: 0.9 });
      const m = markers.current.get(v.id); if (m) m.openPopup();
    }
  };

  // ── chart data ──
  const counts: Record<VState, number> = {
    Moving: 0, Halted: 0, Parked: 0, Offline: 0, Delayed: 0, 'GPS Gap': 0,
    'Provider down': 0, Unreliable: 0,
  };
  vehicles.forEach(v => counts[vState(v)]++);
  // Parked sits next to Halted rather than with the faults, because it is not
  // one: the truck is stopped and its device is working. Provider down is kept
  // separate from Offline for the same reason — one is the truck's problem and
  // the other is ours.
  const statusData = [
    { name: 'Moving', value: counts.Moving, color: '#22A559' },
    { name: 'Halted', value: counts.Halted, color: '#E8941A' },
    { name: 'Parked', value: counts.Parked, color: '#F0B429' },
    { name: 'Delayed', value: counts.Delayed, color: '#B42318' },
    { name: 'Unreliable', value: counts.Unreliable, color: '#DC2626' },
    { name: 'GPS Gap', value: counts['GPS Gap'], color: '#7C3AED' },
    { name: 'Provider down', value: counts['Provider down'], color: '#6B7280' },
    { name: 'Offline', value: counts.Offline, color: '#9AA5B1' },
  ].filter(d => d.value > 0);
  const tripStatus = ['PENDING', 'ACTIVE', 'COMPLETED', 'CANCELLED'].map(s => ({ name: s[0] + s.slice(1).toLowerCase(), value: trips.filter(t => t.status === s).length }));
  const alertTypes: Record<string, number> = {};
  alerts.forEach(a => { alertTypes[a.type] = (alertTypes[a.type] || 0) + 1; });
  const alertData = Object.entries(alertTypes).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }));

  // ── added metrics (Trip Distance Analysis + fleet counts) ──
  // `vehicles` now holds only the trucks on a live trip, so this counts those with
  // a device linked among the ones being followed.
  const deviceCount    = vehicles.filter(v => v.gpsDeviceId).length;
  const totalTrips     = trips.length;
  const pendingTrips   = trips.filter(t => t.status === 'PENDING').length;
  const completedTrips = trips.filter(t => t.status === 'COMPLETED').length;
  let estKm = 0, actKm = 0, devKm = 0;
  trips.forEach(t => {
    const e = km(t.fromLat, t.fromLng, t.toLat, t.toLng) * 1.3;   // planned road distance
    estKm += e;
    if (t.status === 'COMPLETED') {
      const a = (t.totalDistanceKm && t.totalDistanceKm < e * 3) ? t.totalDistanceKm : e * 1.08;
      actKm += a; devKm += Math.abs(a - e);
    }
  });
  const distData = [
    { name: 'Total Estimated KM', value: Math.round(estKm), color: '#38BDF8' },
    { name: 'Total Actual KM',    value: Math.round(actKm), color: '#6366F1' },
    { name: 'Total Deviation KM', value: Math.round(devKm), color: '#22C55E' },
  ].filter(d => d.value > 0);

  // Each card opens the module it counts (only if the signed-in user may open it).
  const KPI: { icon: string; label: string; value: React.ReactNode; c: string; to: string; perm: FleetPermission }[] = [
    { icon: '🚚', label: 'Active Trips', value: stats.activeTrips ?? 0, c: '#22A559', to: '/fleet/trips?status=ACTIVE', perm: 'trip_list' },
    { icon: '🟢', label: 'Moving Now', value: counts.Moving, c: ACCENT, to: '/fleet/reports/devices?health=MOVING', perm: 'reports' },
    { icon: '🅿️', label: 'Idle', value: stats.idleVehicles ?? 0, c: '#E8941A', to: '/fleet/master/vehicles?status=IDLE', perm: 'master' },
    { icon: '📴', label: 'Offline Devices', value: stats.offlineVehicles ?? counts.Offline, c: '#6B7280', to: '/fleet/reports/devices?health=OFFLINE', perm: 'reports' },
    { icon: '⏱️', label: 'Delayed Data', value: stats.delayedVehicles ?? counts.Delayed, c: '#B42318', to: '/fleet/reports/devices?health=DELAYED', perm: 'reports' },
    { icon: '📡', label: 'GPS Gaps', value: stats.gpsGapVehicles ?? counts['GPS Gap'], c: '#7C3AED', to: '/fleet/reports/devices?health=GPS_GAP', perm: 'reports' },
    { icon: '📤', label: 'IFMS Pending', value: ifmsPending ?? '—', c: '#FF2D55', to: '/fleet/ifms', perm: 'trip_list' },
    { icon: '🚨', label: 'Open Alerts', value: stats.unresolvedAlerts ?? alerts.length, c: '#DC3545', to: '/fleet/alerts', perm: 'alerts' },
    { icon: '🌐', label: 'Source Issues', value: stats.unhealthySources ?? 0, c: '#B42318', to: '/fleet/reports?card=sources', perm: 'reports' },
    // The map shows only trucks with a live trip, so say how many that is — otherwise
    // an empty map looks like a fault rather than "nothing is on the road".
    { icon: '📍', label: 'Tracking Now', value: stats.trackingNow ?? vehicles.length, c: '#30B0C7', to: '/fleet/trips?status=ACTIVE', perm: 'trip_list' },
    { icon: '📡', label: 'No. of Devices', value: deviceCount, c: '#0EA5E9', to: '/fleet/reports/devices', perm: 'reports' },
    { icon: '🧾', label: 'Total Trips', value: totalTrips, c: '#6366F1', to: '/fleet/trips', perm: 'trip_list' },
    { icon: '🏭', label: 'At Plant', value: stats.waitingTrips ?? pendingTrips, c: '#F59E0B', to: '/fleet/trips?status=PENDING', perm: 'trip_list' },
    { icon: '✅', label: 'Completed Trips', value: completedTrips, c: '#10B981', to: '/fleet/trips?status=COMPLETED', perm: 'trip_list' },
  ];

  const active = vehicles.filter(v => tripByVeh[v.id]); // vehicles on an active trip (Uber-style cards)

  // ── trips table (search + pagination) ──
  const q = search.trim().toLowerCase();
  const filtered = trips.filter(t => !q || [t.tripNumber, t.tripName, t.vehicle?.vehicleNumber, t.fromLocation, t.toLocation, t.status].some(x => String(x || '').toLowerCase().includes(q)));
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  const fmt = (d?: ISODate | null) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  /** Same instant, stacked: "26 Sep 2026" over "02:08 pm". */
  const fmtCell = (d?: ISODate | null) => {
    if (!d) return <>—</>;
    const x = new Date(d);
    return (
      <>
        {x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        <div style={S.tdSub}>{x.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
      </>
    );
  };

  return (
    <div style={S.page}>
      <style>{`.fx-kpi:hover { transform: translateY(-2px); box-shadow: var(--fx-shadow-lg) !important; }
        .fx-kpi:hover .fx-kpi-go { opacity: 1; transform: translate(2px, -50%); }
        .fx-kpi:focus-visible { outline: 2px solid var(--fx-accent); outline-offset: 2px; }`}</style>
      <div style={S.head}>
        <div><h1 style={S.h1}><span style={S.live} /> Live Fleet Control</h1><p style={S.sub}>Real-time tracking, trips & analytics</p></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {updatedAt && <span style={{ fontSize: 11, color: 'var(--fx-text-3)' }}>updated {updatedAt.toLocaleTimeString()}</span>}
          {refreshFailed && (
            <span
              title={updatedAt
                ? 'The last refresh did not reach the server. What you see is from the time shown, not from now.'
                : 'Could not reach the server, so nothing has loaded yet.'}
              style={{ fontSize: 11, fontWeight: 600, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)', padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}
            >
              could not refresh
            </span>
          )}
          <button style={{ ...S.refresh, opacity: refreshing ? 0.65 : 1, cursor: refreshing ? 'default' : 'pointer' }} onClick={loadAll} disabled={refreshing}>
            <span className={refreshing ? 'spin' : ''} style={{ display: 'inline-block', marginRight: 4 }}>↻</span>{refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={S.kpis}>
        {KPI.map(k => {
          const body = (
            <>
              <div style={S.kpiIcon}>{k.icon}</div>
              <div style={{ minWidth: 0 }}><div style={{ ...S.kpiVal, color: k.c }}>{k.value}</div><div style={S.kpiLbl}>{k.label}</div></div>
              {can(user, k.perm) && <span className="fx-kpi-go" style={S.kpiGo}>›</span>}
            </>
          );
          const style = { ...S.kpi, borderTop: `3px solid ${k.c}` };
          return can(user, k.perm)
            ? <Link key={k.label} to={k.to} className="fx-kpi" style={style} title={`Open ${k.label}`}>{body}</Link>
            : <div key={k.label} style={style}>{body}</div>;
        })}
      </div>

      {/* Charts */}
      <div style={S.charts}>
        <div style={S.card}>
          <div style={S.cardT}>Vehicle Status</div>
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={statusData.length ? statusData : [{ name: 'None', value: 1, color: '#8E8E9340' }]} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2}>
                {(statusData.length ? statusData : [{ color: '#8E8E9340' }]).map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div style={S.legend}>
            {statusData.map(d => <span key={d.name} style={S.legItem}><span style={{ ...S.legDot, background: d.color }} />{d.name} {d.value}</span>)}
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardT}>Trips by Status</div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={tripStatus} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8E8E93' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#8E8E93' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill={ACCENT} radius={[6, 6, 0, 0]} barSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={S.card}>
          <div style={S.cardT}>Alerts by Type</div>
          {alertData.length ? (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={alertData} layout="vertical" margin={{ top: 8, right: 12, left: 10, bottom: 0 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#8E8E93' }} width={95} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#DC3545" radius={[0, 6, 6, 0]} barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={S.noData}>No alerts ✓</div>}
        </div>

        <div style={S.card}>
          <div style={S.cardT}>Trip Distance Analysis (KM)</div>
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={distData.length ? distData : [{ name: 'None', value: 1, color: '#8E8E9340' }]} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2}>
                {(distData.length ? distData : [{ color: '#8E8E9340' }]).map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip formatter={(v) => `${v} KM`} />
            </PieChart>
          </ResponsiveContainer>
          <div style={S.legend}>
            {distData.map(d => <span key={d.name} style={S.legItem}><span style={{ ...S.legDot, background: d.color }} />{d.name}: {d.value}</span>)}
          </div>
        </div>

        {/* The charts leave a gap on the second row; the assistant fills it. */}
        <div style={S.aiSlot}><FleetAiPanel /></div>
      </div>

      {sourceStatuses.length > 0 && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={S.cardT}>Data Source Health</div>
          <div style={S.sourceGrid}>
            {sourceStatuses.map((s) => (
              <div key={s.source} style={S.sourceCard}>
                <div style={S.sourceTop}>
                  <b style={{ color: 'var(--fx-text)' }}>{s.source}</b>
                  <span style={{ ...S.sourceBadge, background: s.status === 'HEALTHY' ? '#22A559' : s.status === 'DEGRADED' ? '#E8941A' : '#B42318' }}>{s.status}</span>
                </div>
                <div style={S.sourceMeta}>Last poll: {fmt(s.lastPollAt)}</div>
                <div style={S.sourceMeta}>Last success: {fmt(s.lastSuccessAt)}</div>
                <div style={S.sourceMeta}>Forwarded: {s.lastDevicesForwarded ?? 0} / {s.lastDevicesSeen ?? 0}</div>
                {s.lastError && <div style={S.sourceErr}>{s.lastError}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live tracking — Ola/Uber style */}
      <div style={{ ...S.trackWrap, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ ...S.cardT, padding: '14px 16px 0' }}>🗺️ Live Map</div>
          <div ref={mapEl} style={{ width: '100%', height: 440 }} />
          <div style={S.mapLegend}>
            <span style={S.mlItem}><span style={{ ...S.legDot, background: '#22A559' }} />Moving</span>
            <span style={S.mlItem}><span style={{ ...S.legDot, background: '#E8941A' }} />Halted</span>
            <span style={S.mlItem}><span style={{ ...S.legDot, background: '#B42318' }} />Delayed</span>
            <span style={S.mlItem}><span style={{ ...S.legDot, background: '#7C3AED' }} />GPS Gap</span>
            <span style={S.mlItem}><span style={{ ...S.legDot, background: '#9AA5B1' }} />Offline</span>
          </div>
        </div>

        <div style={S.trackList}>
          <div style={S.trackHead}>🚛 Active Tracking <span style={S.trackCount}>{active.length}</span></div>
          {active.map(v => {
            const t = tripByVeh[v.id];
            const total = km(t.fromLat, t.fromLng, t.toLat, t.toLng);
            const done = v.currentLat != null && v.currentLng != null ? km(t.fromLat, t.fromLng, v.currentLat, v.currentLng) : 0;
            const pct = Math.min(Math.round((done / (total || 1)) * 100), 100);
            const speed = Math.round(v.currentSpeed || 0);
            const eta = speed > 5 ? Math.round((total - done) / speed * 60) : null;
            return (
              <div key={v.id} style={{ ...S.tcard, ...(sel === v.id ? S.tcardSel : {}) }} onClick={() => trackRoute(v, 'vehicle')}>
                <div style={S.tcTop}>
                  <span style={{ ...S.tcDot, background: vColor(v) }} />
                  <b style={S.tcVeh}>{v.vehicleNumber}</b>
                  <span style={S.tcSpeed}>{vState(v) === 'Moving' ? `${speed} km/h` : v.healthLabel || vState(v)}</span>
                </div>
                <div style={S.tcDriver}>👤 {v.driverName}</div>
                {/* Only when there is something to explain. A truck that looks
                    quiet usually has a reason, and until now it was only in the
                    database. */}
                {(v.recentRejections ?? 0) > 0 && (
                  <button type="button" style={S.tcDiag}
                    onClick={(e) => { e.stopPropagation(); setDiag({ id: v.id, label: v.vehicleNumber }); }}>
                    {v.recentRejections} GPS fix(es) refused — why?
                  </button>
                )}
                <div style={S.tcRoute}>{t.fromLocation} <span style={{ color: 'var(--fx-text-3)' }}>→</span> {t.toLocation}</div>
                {(v.lastIssueType || (v.dataDelayMinutes ?? 0) > 0) && (
                  <div style={S.tcWarn}>
                    {v.lastIssueType ? `${v.lastIssueType.replace(/_/g, ' ')}.` : null}
                    {(v.dataDelayMinutes ?? 0) > 0 ? ` Data lag ${v.dataDelayMinutes} min.` : ''}
                  </div>
                )}
                <div style={S.tcBar}><div style={{ ...S.tcFill, width: `${pct}%` }} /></div>
                <div style={S.tcMeta}>
                  <span>{pct}% done</span>
                  <span>{eta != null ? `~${eta >= 60 ? Math.floor(eta / 60) + ' hr ' + (eta % 60) + ' min' : eta + ' min'} left` : `${(total - done).toFixed(0)} km left`}</span>
                </div>
                <div style={S.tcActions}>
                  <span style={S.tcTrack} onClick={(e) => { e.stopPropagation(); trackRoute(v); }}>📍 Track Route</span>
                  <span style={S.tcDetail} onClick={(e) => { e.stopPropagation(); navigate(`/fleet/trips/${t.id}`); }}>Trip details →</span>
                </div>
              </div>
            );
          })}
          {active.length === 0 && <div style={S.noTrack}>No active trips right now.<br /><span style={{ fontSize: 12 }}>Create a trip to start tracking.</span></div>}
        </div>
      </div>

      {/* All Trips table — search + pagination */}
      <div style={{ ...S.card, marginTop: 16, padding: 0, overflow: 'hidden' }}>
        <div style={S.tableHead}>
          <div style={S.cardT}>All Trips</div>
          <input style={S.searchBox} placeholder="🔍 Search…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead><tr>{['Sr', 'Booking', 'Name', 'Vehicle', 'Origin', 'Destination', 'Start', 'End', 'Distance', 'Status'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {pageRows.map((t, i) => (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/fleet/trips/${t.id}`)}>
                  <td style={S.td}>{(page - 1) * pageSize + i + 1}</td>
                  <td style={S.td}><b style={{ color: ACCENT }}>{t.tripNumber}</b><div style={S.tdSub}>{fmt(t.createdAt)}</div></td>
                  <td style={S.tdName} title={t.tripName || ''}>{t.tripName || 'Trip'}</td>
                  <td style={S.td}>{t.vehicle?.vehicleNumber}<div style={S.tdSub}>{t.vehicle?.driverName}</div></td>
                  <td style={S.tdPlace} title={t.fromLocation || ''}>{shortPlace(t.fromLocation)}</td>
                  <td style={S.tdPlace} title={t.toLocation || ''}>{shortPlace(t.toLocation)}</td>
                  <td style={S.td}>{fmtCell(t.startedAt || t.estStart)}</td>
                  <td style={S.td}>{fmtCell(t.completedAt || t.estEnd)}</td>
                  <td style={S.td}>{t.totalDistanceKm ? `${t.totalDistanceKm} km` : '—'}</td>
                  <td style={S.td}><span style={{ ...S.stBadge, background: STATUS_BG[t.status] || '#6B7280' }}>{t.status}</span></td>
                </tr>
              ))}
              {pageRows.length === 0 && <tr><td colSpan={10} style={S.emptyTd}>No trips found</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={S.pager}>
          <span style={S.pagerInfo}>Showing {filtered.length ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select style={S.pageSel} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[10, 25, 50].map(n => <option key={n} value={n}>Show {n}</option>)}
            </select>
            <button style={S.pageBtn} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
            <span style={S.pagerInfo}>{page} / {pages}</span>
            <button style={S.pageBtn} disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))}>›</button>
          </div>
        </div>
      </div>

      {diag && <GpsDiagnostics vehicleId={diag.id} label={diag.label} onClose={() => setDiag(null)} />}
    </div>
  );
}

/**
 * The fixes the server refused for one truck, and why.
 *
 * The question this answers is "the truck looks quiet — is the tracking broken?"
 * Usually it is not: the device has been sending positions three hundred
 * kilometres from the last one, and every one of them was refused. Until now
 * that was only in the database.
 */
function GpsDiagnostics({ vehicleId, label, onClose }: { vehicleId: string; label: string; onClose: () => void }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof getVehicleRejections>>['data'] | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    getVehicleRejections(vehicleId, 24)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(apiError(e, 'Could not load the diagnostics')); });
    return () => { alive = false; };
  }, [vehicleId]);

  return (
    <div style={S.diagBack} onClick={onClose}>
      <div style={S.diagBox} onClick={(e) => e.stopPropagation()}>
        <div style={S.diagHead}>
          <b>{label} — refused GPS fixes</b>
          <button type="button" style={S.diagClose} onClick={onClose}>✕</button>
        </div>
        {err && <div style={{ color: 'var(--fx-red)', fontSize: 12.5 }}>{err}</div>}
        {!data && !err && <div style={{ fontSize: 12.5, color: 'var(--fx-text-2)' }}>Loading…</div>}
        {data && data.total === 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--fx-text-2)' }}>
            Nothing was refused in the last {data.hours} hours. If this truck looks quiet, its device is not
            sending at all rather than sending something unusable.
          </div>
        )}
        {data && data.total > 0 && (
          <>
            <div style={{ fontSize: 12, color: 'var(--fx-text-2)', marginBottom: 8 }}>
              {data.total} in the last {data.hours} hours —{' '}
              {Object.entries(data.byReason).map(([k, n]) => `${n} ${k.toLowerCase().replace(/_/g, ' ')}`).join(', ')}
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                <thead>
                  <tr style={{ background: 'var(--fx-surface-2)' }}>
                    {['When', 'Why', 'Detail'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '5px 7px', color: 'var(--fx-text-2)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rejections.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--fx-border)' }}>
                      <td style={{ padding: '5px 7px', whiteSpace: 'nowrap' }}>{new Date(r.receivedAt).toLocaleString()}</td>
                      <td style={{ padding: '5px 7px', whiteSpace: 'nowrap' }}>{r.reason.toLowerCase().replace(/_/g, ' ')}</td>
                      <td style={{ padding: '5px 7px' }}>{r.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const STATUS_BG: Record<string, string> = { PENDING: '#E8941A', ACTIVE: '#22A559', COMPLETED: '#6B7280', CANCELLED: '#DC3545' };

/**
 * "Bagging Building, Fertilizer Corporation Of India, Godavarikhani, Ramagundam,
 *  Telangana, India" → "Bagging Building, Ramagundam".
 *
 * Every dispatch leaves from the same plant, so printing that address on every row
 * spent most of the table's width saying the same thing and pushed the columns that
 * differ off the screen. The place and the town it is in are what a reader needs;
 * the full text stays on hover.
 */
function shortPlace(s: string | null | undefined): string {
  if (!s) return '—';
  const parts = s.split(',').map((x) => x.trim()).filter((x) => x && !/^india$/i.test(x));
  if (parts.length <= 2) return parts.join(', ') || '—';
  return `${parts[0]}, ${parts[parts.length - 2]}`;
}

const S = css({
  page: { padding: '22px 22px 40px', background: 'var(--fx-bg)', minHeight: '100vh' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  h1: { fontSize: 22, fontWeight: 800, color: 'var(--fx-text)', margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  live: { width: 11, height: 11, borderRadius: '50%', background: '#22C55E', boxShadow: '0 0 0 4px rgba(34,197,94,0.25)', animation: 'livePulse 2s infinite', display: 'inline-block' },
  sub: { fontSize: 13, color: 'var(--fx-text-2)', margin: '3px 0 0' },
  refresh: { background: 'var(--fx-surface)', border: '1px solid var(--fx-border-strong)', borderRadius: 10, padding: '9px 15px', fontSize: 13, fontWeight: 600, color: 'var(--fx-text)', cursor: 'pointer' },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 16 },
  kpi: { position: 'relative', background: 'var(--fx-surface)', borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: 'var(--fx-shadow)', textDecoration: 'none', color: 'inherit', transition: 'transform .15s, box-shadow .15s' },
  kpiGo: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 17, color: 'var(--fx-text-3)', opacity: 0.35, transition: 'opacity .15s, transform .15s' },
  kpiIcon: { fontSize: 22 },
  kpiVal: { fontSize: 26, fontWeight: 800, lineHeight: 1 },
  kpiLbl: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 3 },
  charts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 14, marginBottom: 16 },
  // Two columns wide where there is room, so the chat has space to read; it
  // falls back to one column on a narrow screen like every other card.
  aiSlot: { gridColumn: 'span 2', minWidth: 0 },
  card: { background: 'var(--fx-surface)', borderRadius: 14, padding: 16, boxShadow: 'var(--fx-shadow)' },
  cardT: { fontSize: 14, fontWeight: 700, color: 'var(--fx-text)', marginBottom: 10 },
  legend: { display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', marginTop: 4 },
  legItem: { fontSize: 12, color: 'var(--fx-text-2)', display: 'flex', alignItems: 'center', gap: 5 },
  legDot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  noData: { height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fx-text-3)', fontSize: 14 },
  sourceGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 },
  sourceCard: { border: '1px solid var(--fx-border)', borderRadius: 12, padding: 14, background: 'var(--fx-surface-2)' },
  sourceTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 },
  sourceBadge: { color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 16, padding: '3px 8px' },
  sourceMeta: { fontSize: 12, color: 'var(--fx-text-2)', marginBottom: 4 },
  sourceErr: { fontSize: 12, color: '#B42318', marginTop: 6 },
  trackWrap: { display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 14, alignItems: 'start' },
  mapLegend: { display: 'flex', gap: 16, padding: '10px 16px', borderTop: '1px solid var(--fx-border)' },
  mlItem: { fontSize: 12, color: 'var(--fx-text-2)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 },
  trackList: { background: 'var(--fx-surface)', borderRadius: 14, padding: 14, boxShadow: 'var(--fx-shadow)', maxHeight: 560, overflow: 'auto' },
  trackHead: { fontSize: 14, fontWeight: 700, color: 'var(--fx-text)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 },
  trackCount: { background: NAVY, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 9px' },
  tcard: { border: '1px solid var(--fx-border)', borderRadius: 12, padding: 13, marginBottom: 10, cursor: 'pointer', transition: 'all 0.15s' },
  tcardSel: { borderColor: ACCENT, boxShadow: '0 4px 16px rgba(21,96,189,0.18)', background: 'var(--fx-surface-2)' },
  tcTop: { display: 'flex', alignItems: 'center', gap: 8 },
  tcDot: { width: 10, height: 10, borderRadius: '50%' },
  tcVeh: { fontSize: 14, color: 'var(--fx-text)', flex: 1 },
  tcSpeed: { fontSize: 12, fontWeight: 700, color: ACCENT },
  tcDriver: { fontSize: 12, color: 'var(--fx-text-2)', margin: '6px 0 3px' },
  tcDiag: {
    display: 'block', marginTop: 4, padding: 0, border: 'none', background: 'none',
    font: 'inherit', fontSize: 11.5, fontWeight: 600, color: 'var(--fx-orange)',
    cursor: 'pointer', textAlign: 'left' as const, textDecoration: 'underline',
  },

  diagBack: {
    position: 'fixed' as const, inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  diagBox: {
    background: 'var(--fx-surface)', borderRadius: 12, boxShadow: 'var(--fx-shadow-lg)',
    width: 'min(760px, 100%)', maxHeight: '80vh', overflow: 'auto', padding: 16,
  },
  diagHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 10, fontSize: 14,
  },
  diagClose: {
    border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--fx-text-2)',
  },
  tcRoute: { fontSize: 12.5, color: 'var(--fx-text)', fontWeight: 500, marginBottom: 8 },
  tcWarn: { fontSize: 11.5, color: '#B42318', marginBottom: 8 },
  tcBar: { height: 7, background: 'var(--fx-surface-2)', borderRadius: 6, overflow: 'hidden' },
  tcFill: { height: '100%', background: 'linear-gradient(90deg,#1560BD,#22A559)', borderRadius: 6, transition: 'width 1s' },
  tcMeta: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--fx-text-2)', marginTop: 5 },
  tcActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 9 },
  tcTrack: { fontSize: 12, fontWeight: 700, color: '#fff', background: ACCENT, borderRadius: 8, padding: '5px 12px' },
  tcDetail: { fontSize: 12, fontWeight: 600, color: ACCENT },
  noTrack: { textAlign: 'center', color: 'var(--fx-text-3)', fontSize: 14, padding: '30px 10px' },
  // ── trips table ──
  tableHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 4px', gap: 10, flexWrap: 'wrap' },
  searchBox: { padding: '7px 11px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 12.5, outline: 'none', minWidth: 180, fontFamily: 'inherit' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 900 },
  th: { textAlign: 'left', padding: '10px 8px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: '#fff', textTransform: 'uppercase', background: '#0A3A7A', whiteSpace: 'nowrap' },
  td: { padding: '10px 8px', borderBottom: '1px solid var(--fx-border)', color: 'var(--fx-text)', verticalAlign: 'top', whiteSpace: 'nowrap' },
  tdSub: { fontSize: 10.5, color: 'var(--fx-text-3)', marginTop: 2 },
  // Long-ish but bounded: an unusually long dealer name is cut with an ellipsis
  // rather than being allowed to widen the whole table again.
  // A descriptive trip name ("Live test — Ramagundam to Adilabad") would otherwise
  // widen the table on its own; the full name is on hover.
  tdName: { padding: '10px 8px', borderBottom: '1px solid var(--fx-border)', color: 'var(--fx-text)', verticalAlign: 'top', whiteSpace: 'nowrap', maxWidth: 125, overflow: 'hidden', textOverflow: 'ellipsis' },
  tdPlace: { padding: '10px 8px', borderBottom: '1px solid var(--fx-border)', color: 'var(--fx-text)', verticalAlign: 'top', whiteSpace: 'nowrap', maxWidth: 168, overflow: 'hidden', textOverflow: 'ellipsis' },
  stBadge: { color: '#fff', fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 20, display: 'inline-block' },
  emptyTd: { padding: 26, textAlign: 'center', color: 'var(--fx-text-3)' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: 10, flexWrap: 'wrap' },
  pagerInfo: { fontSize: 12, color: 'var(--fx-text-2)' },
  pageSel: { padding: '6px 8px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 12, cursor: 'pointer' },
  pageBtn: { width: 30, height: 30, borderRadius: 8, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-surface)', cursor: 'pointer', fontSize: 15, color: 'var(--fx-text)' },
});
